import type { SearchClient } from 'instantsearch.js'

const TRACKED_SEARCH_ERROR = Symbol('tracked-search-error')

class MalformedSearchResponseError extends Error {
  constructor(message: string) {
    super(`Malformed search response: ${message}`)
    this.name = 'MalformedSearchResponseError'
  }
}

export class TrackedSearchError extends Error {
  readonly [TRACKED_SEARCH_ERROR] = true

  constructor(
    message: string,
    readonly hasActiveRefinements: boolean,
    readonly isLatestRequest: () => boolean,
    options?: ErrorOptions,
  ) {
    super(message, options)
    this.name = 'TrackedSearchError'
  }
}

const hasFilterValue = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.some(hasFilterValue)
  }

  return typeof value === 'string'
    ? value.trim().length > 0
    : value !== null && value !== undefined
}

interface TrackedSearchRequest {
  params?: {
    facetFilters?: unknown
    numericFilters?: unknown
    tagFilters?: unknown
  }
}

export interface SearchRequestTracker {
  latestRequestId: number
}

const requestHasActiveRefinements = (requests: TrackedSearchRequest[]) =>
  requests.some(request => {
    const { facetFilters, numericFilters, tagFilters } = request.params ?? {}

    return [facetFilters, numericFilters, tagFilters].some(hasFilterValue)
  })

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error)

const hasSearchHits = (result: unknown) =>
  typeof result === 'object' &&
  result !== null &&
  !Array.isArray(result) &&
  Array.isArray((result as { hits?: unknown }).hits)

const assertSearchResponse = (response: unknown, requestCount: number) => {
  const results = (response as { results?: unknown } | null)?.results

  if (!Array.isArray(results)) {
    throw new MalformedSearchResponseError('results is not an array')
  }

  if (results.length !== requestCount) {
    throw new MalformedSearchResponseError(
      `expected ${requestCount} results, received ${results.length}`,
    )
  }

  if (!results.every(hasSearchHits)) {
    throw new MalformedSearchResponseError('a result is missing its hits array')
  }
}

export const isTrackedSearchError = (
  error: unknown,
): error is TrackedSearchError =>
  error instanceof Error &&
  TRACKED_SEARCH_ERROR in error &&
  error[TRACKED_SEARCH_ERROR] === true

export const createTrackedSearchClient = (
  searchClient: SearchClient,
  requestTracker: SearchRequestTracker = { latestRequestId: 0 },
): SearchClient => {
  const search = (async (requests: TrackedSearchRequest[]) => {
    requestTracker.latestRequestId += 1

    const requestId = requestTracker.latestRequestId
    const hasActiveRefinements = requestHasActiveRefinements(requests)

    try {
      const response = await searchClient.search(requests)

      assertSearchResponse(response, requests.length)

      return response
    } catch (error) {
      throw new TrackedSearchError(
        getErrorMessage(error),
        hasActiveRefinements,
        () => requestId === requestTracker.latestRequestId,
        { cause: error },
      )
    }
  }) as SearchClient['search']

  return {
    ...searchClient,
    search,
  }
}
